import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import {
  QUESTION_MEDIA_RULES,
  normalizeCrosswordQuestion,
  questionMediaUrl,
} from "../../../functions/crosswordQuestions";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

const QA_MAX_PAIRS = 12;
const QA_MIN_PAIRS = 2;
const QA_QUESTION_MAX = 80;
const QA_ANSWER_MAX = 15;
const QA_HEADING_MAX = 60;
const QA_SENTENCE_MAX = 120;

const QA_TYPE_OPTIONS = [
  { value: "text", label: "Text", icon: "fa-font" },
  { value: "image", label: "Image", icon: "fa-image" },
  { value: "video", label: "Video", icon: "fa-video" },
];

let pairUidSeq = 0;
const nextPairUid = () => `pair-${++pairUidSeq}`;

const UPLOAD_PREFIX = "__UPLOAD__:";
const isUploadRef = (media) => typeof media === "string" && media.startsWith(UPLOAD_PREFIX);

/** Editor row: saved fields + a local uid (stable key while rows are added/removed)
 *  + file/previewUrl for a just-picked file. Until saved, `media` holds an
 *  `__UPLOAD__:<token>` ref that the backend swaps for the S3 filename. */
function toEditorPair(raw) {
  return { ...normalizeCrosswordQuestion(raw), uid: nextPairUid(), file: null, previewUrl: "" };
}

/** Upload-safe copy of a picked file: drops spaces/brackets etc. from the name. */
function renameFileForUpload(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  return new File([file], ext ? `${id}.${ext}` : id, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified ?? Date.now(),
  });
}

/** What actually gets stored in questions_and_answers for one row. */
function toSavedPair(p) {
  const answer = p.answer.trim();
  if (p.type === "text") return { type: "text", question: p.question.trim(), answer };
  return {
    type: p.type,
    answer,
    media: p.media,
    heading: p.heading.trim(),
    sentence: p.sentence.trim(),
  };
}

/** First validation problem across all rows, or null. Messages name the pair number. */
function findPairError(pairs) {
  for (let i = 0; i < pairs.length; i++) {
    const p = pairs[i];
    const n = `Pair ${i + 1}`;
    if (p.type === "text") {
      if (!p.question.trim()) return `${n}: please enter the question.`;
      if (p.question.trim().length > QA_QUESTION_MAX)
        return `${n}: question must be ${QA_QUESTION_MAX} characters or fewer.`;
    } else {
      if (!p.media) return `${n}: please upload ${p.type === "image" ? "an image" : "a video"}.`;
      if (isUploadRef(p.media) && !p.file) return `${n}: please re-upload the ${p.type}.`;
      if (p.heading.trim().length > QA_HEADING_MAX)
        return `${n}: heading must be ${QA_HEADING_MAX} characters or fewer.`;
      if (p.sentence.trim().length > QA_SENTENCE_MAX)
        return `${n}: supporting sentence must be ${QA_SENTENCE_MAX} characters or fewer.`;
    }
    if (!p.answer.trim()) return `${n}: please enter the answer.`;
    if (p.answer.trim().length > QA_ANSWER_MAX)
      return `${n}: answer must be ${QA_ANSWER_MAX} characters or fewer.`;
  }
  return null;
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [tourOpen, setTourOpen] = useState(false);

  const [pairs, setPairs] = useState([]);
  const [qaSaving, setQaSaving] = useState(false);
  const fileInputs = useRef({});

  /** Free local preview blobs when they're replaced or the page unmounts. */
  const previewUrlsRef = useRef([]);
  useEffect(() => {
    const live = new Set(pairs.map((p) => p.previewUrl).filter(Boolean));
    previewUrlsRef.current.forEach((url) => {
      if (!live.has(url)) URL.revokeObjectURL(url);
    });
    previewUrlsRef.current = [...live];
  }, [pairs]);
  useEffect(
    () => () => previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url)),
    []
  );

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const loadedPairs = Array.isArray(data.questions_and_answers)
      ? data.questions_and_answers
      : [];
    setPairs(loadedPairs.map(toEditorPair));
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (!shouldAutoStartThemeUpdateTour()) return;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = () => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  };

  const addPair = () => {
    if (pairs.length >= QA_MAX_PAIRS) {
      Swal.fire("Limit reached", `You reached the limit of ${QA_MAX_PAIRS} pairs`, "warning");
      return;
    }
    setPairs((prev) => [...prev, toEditorPair({ type: "text" })]);
  };

  const removePair = (uid) => {
    setPairs((prev) => prev.filter((p) => p.uid !== uid));
  };

  const clearAllPairs = () => {
    setPairs([]);
  };

  const updatePair = (uid, key, value) => {
    setPairs((prev) => prev.map((p) => (p.uid === uid ? { ...p, [key]: value } : p)));
  };

  /** Changing type drops the uploaded file — an image can't serve a video clue (or vice versa).
   *  Text question, heading and sentence are kept so switching back loses nothing typed. */
  const changePairType = (uid, type) => {
    setPairs((prev) =>
      prev.map((p) =>
        p.uid === uid && p.type !== type
          ? { ...p, type, media: "", file: null, previewUrl: "" }
          : p
      )
    );
  };

  /** Picking a file only stages it (preview + upload ref); it goes to S3 on Save. */
  const handleMediaPick = (pair, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const rules = QUESTION_MEDIA_RULES[pair.type];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!rules.extensions.includes(ext)) {
      Swal.fire(
        "Invalid File",
        `Allowed formats: ${rules.extensions.map((x) => x.toUpperCase()).join(", ")}.`,
        "error"
      );
      return;
    }
    if (file.size > rules.maxMb * 1024 * 1024) {
      Swal.fire(
        "File Too Large",
        `${pair.type === "image" ? "Images" : "Videos"} must be under ${rules.maxMb} MB.`,
        "error"
      );
      return;
    }

    const renamed = renameFileForUpload(file);
    const token = `qamedia_${pair.uid}_${Date.now()}`;
    setPairs((prev) =>
      prev.map((p) =>
        p.uid === pair.uid && p.type === pair.type
          ? {
              ...p,
              media: `${UPLOAD_PREFIX}${token}`,
              file: renamed,
              previewUrl: URL.createObjectURL(renamed),
            }
          : p
      )
    );
  };

  const saveQaPairs = async () => {
    if (pairs.length < QA_MIN_PAIRS) {
      Swal.fire("Validation Error!", "Please add at least two pairs before saving.", "warning");
      return;
    }
    const pairError = findPairError(pairs);
    if (pairError) {
      Swal.fire("Validation Error!", pairError, "warning");
      return;
    }

    setQaSaving(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append(
        "extraData",
        JSON.stringify({ questions_and_answers: pairs.map(toSavedPair) })
      );
      pairs.forEach((p) => {
        if (p.type !== "text" && isUploadRef(p.media) && p.file) {
          formData.append(p.media.slice(UPLOAD_PREFIX.length), p.file);
        }
      });
      await uploadThemeMedia({ formData, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success!", "Data has been saved.", "success");
      navigate("/admin/gridpattern");
    } catch (e) {
      Swal.fire("Error!", e.message || "An error occurred while saving data.", "error");
    } finally {
      setQaSaving(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="themeupdate-section-heading" data-tour="tour-gc-header">
        <h2 className="themeupdate-section-title">Question Settings</h2>
      </div>

      <div className="themeupdate-panel-wide down-across-panel" data-tour="tour-gc-settings">
        <p className="qa-char-limit-hint">
          * Maximum character limit: Question — {QA_QUESTION_MAX} characters, Answer —{" "}
          {QA_ANSWER_MAX} characters, Heading — {QA_HEADING_MAX} characters, Supporting
          sentence — {QA_SENTENCE_MAX} characters.
        </p>

        <div className="qa-pairs-columns">
          {pairs.map((pair, index) => {
            const isMedia = pair.type !== "text";
            const uploading = qaSaving && Boolean(pair.file);
            const rules = QUESTION_MEDIA_RULES[pair.type];
            const mediaSrc = pair.previewUrl || questionMediaUrl(pair.media);
            return (
              <div className="qa-pair-card qa-pair-card--typed" key={pair.uid}>
                <div className="qa-pair-head">
                  <span className="qa-pair-number">Pair {index + 1}</span>
                  <button
                    type="button"
                    className="qa-pair-delete"
                    onClick={() => removePair(pair.uid)}
                    aria-label={`Delete pair ${index + 1}`}
                  >
                    <i className="far fa-trash-alt" aria-hidden />
                  </button>
                </div>

                <label>Type</label>
                <div className="qa-type-toggle" role="radiogroup" aria-label="Question type">
                  {QA_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={pair.type === opt.value}
                      className={`qa-type-btn${pair.type === opt.value ? " is-active" : ""}`}
                      onClick={() => changePairType(pair.uid, opt.value)}
                      disabled={qaSaving}
                    >
                      <i className={`fa-solid ${opt.icon}`} aria-hidden /> {opt.label}
                    </button>
                  ))}
                </div>

                {!isMedia ? (
                  <>
                    <label>Question</label>
                    <input
                      type="text"
                      maxLength={QA_QUESTION_MAX}
                      value={pair.question}
                      placeholder="Clue, sentence or question"
                      onChange={(e) => updatePair(pair.uid, "question", e.target.value)}
                    />
                  </>
                ) : (
                  <>
                    <label>{pair.type === "image" ? "Image" : "Video"}</label>
                    <div className="qa-media-field">
                      {mediaSrc ? (
                        <div className="qa-media-preview">
                          {pair.type === "image" ? (
                            <img src={mediaSrc} alt={pair.heading || `Pair ${index + 1} clue`} />
                          ) : (
                            <video src={mediaSrc} controls muted playsInline preload="metadata" />
                          )}
                        </div>
                      ) : null}
                      <div className="qa-media-actions">
                        <button
                          type="button"
                          className="qa-media-btn"
                          onClick={() => fileInputs.current[pair.uid]?.click()}
                          disabled={qaSaving}
                        >
                          <i
                            className={`fa-solid ${uploading ? "fa-spinner fa-spin" : "fa-upload"}`}
                            aria-hidden
                          />{" "}
                          {uploading
                            ? "Uploading…"
                            : pair.media
                              ? `Replace ${pair.type}`
                              : `Upload ${pair.type}`}
                        </button>
                        <span className="qa-media-hint">
                          {rules.extensions.map((x) => x.toUpperCase()).join(", ")} · max{" "}
                          {rules.maxMb} MB
                        </span>
                      </div>
                      <input
                        ref={(el) => {
                          if (el) fileInputs.current[pair.uid] = el;
                          else delete fileInputs.current[pair.uid];
                        }}
                        type="file"
                        accept={rules.accept}
                        hidden
                        onChange={(e) => handleMediaPick(pair, e)}
                      />
                    </div>

                    <label>Heading</label>
                    <input
                      type="text"
                      maxLength={QA_HEADING_MAX}
                      value={pair.heading}
                      placeholder="Optional"
                      onChange={(e) => updatePair(pair.uid, "heading", e.target.value)}
                    />

                    <label>Sentence</label>
                    <input
                      type="text"
                      maxLength={QA_SENTENCE_MAX}
                      value={pair.sentence}
                      placeholder="Optional supporting sentence"
                      onChange={(e) => updatePair(pair.uid, "sentence", e.target.value)}
                    />
                  </>
                )}

                <label>Answer</label>
                <input
                  type="text"
                  maxLength={QA_ANSWER_MAX}
                  value={pair.answer}
                  onChange={(e) => updatePair(pair.uid, "answer", e.target.value)}
                />
              </div>
            );
          })}
        </div>

        <div className="qa-pairs-actions">
          <button type="button" className="qa-btn qa-btn-outline" onClick={addPair}>
            Add Pair
          </button>
          <button
            type="button"
            className="qa-btn qa-btn-primary"
            onClick={saveQaPairs}
            disabled={qaSaving}
          >
            {qaSaving ? "Saving..." : "Save and Continue"}
          </button>
          <button type="button" className="qa-btn qa-btn-primary" onClick={clearAllPairs}>
            Clear All
          </button>
        </div>
      </div>

      <ThemeUpdateFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default ThemeUpdate;
